import { apiSlice } from "../store/api/adminApi";

export type AdminEventLink = {
    id: number
    link: string
    isBase: boolean
    previousLinks: Array<{ id: number; link: string; archivedDate: string | null }>
    referralLink: {
        id: number
        name: string
        customRef: string | null
        isDeleted: boolean
        isPersonalLink: boolean
        creator: { id: number; name: string; surname: string; phone: string } | null
    } | null
}

export const eventApi = apiSlice.injectEndpoints({
    endpoints: (build)=>({
        fetchEvents: build.query<any, any>({
            query: (body: {
                type: string, 
                skip: number,
                take: number,
                eventId?: number,
                filter: string
            }) => ({
                url: `/admin/events/list/${body.type}?${body?.eventId ? `eventId=${body.eventId}&` : ''}skip=${body.skip || 0}&take=${body.take || 10}${body.filter || ""}`
            }),
        }),
        fetchEventsCount: build.query<any, any>({
            query: () => ({
                url: `/admin/events/count/`
            }),
        }),
        hideEvent: build.mutation({
            query: (id: string | number) => ({
                url: `/admin/events/hide/${id}`,
                method: 'PUT',
            }),
        }),
        changeEventPriority: build.mutation({
            query: (id: string | number) => ({
                url: `/admin/events/priority/${id}`,
                method: 'PUT',
            }),
        }),
        showEvent: build.mutation({
            query: (id: string | number) => ({
                url: `/admin/events/show/${id}`,
                method: 'PUT',
            }),
        }),
        resetEvent: build.mutation({
            query: (id: string | number) => ({
                url: `/admin/events/reset/${id}`,
                method: 'PUT',
            }),
        }),
        confirmEvent: build.mutation({
            query: (id: string | number) => ({
                url: `/admin/events/confirm/${id}`,
                method: 'PUT',
            }),
        }),
        changeEventStatus: build.mutation<any, { id: number; status: string }>({
            query: ({ id, status }) => ({
                url: `/admin/events/${id}/status`,
                method: 'PUT',
                body: { status },
            }),
        }),
        getEventSettings: build.query<any, { id: number }>({
            query: ({ id }) => ({
                url: `/admin/events/${id}/settings`,
                method: 'GET',
            }),
        }),
        getEventLinks: build.query<AdminEventLink[], { id: number }>({
            query: ({ id }) => ({
                url: `/admin/events/${id}/links`,
                method: 'GET',
            }),
        }),
        changeEventLink: build.mutation<any, { id: number; linkId: number; link: string }>({
            query: ({ id, linkId, link }) => ({
                url: `/admin/events/${id}/links/${linkId}`,
                method: 'PUT',
                body: { link },
            }),
        }),
        deleteEventPreviousLink: build.mutation<any, { id: number; linkId: number }>({
            query: ({ id, linkId }) => ({
                url: `/admin/events/${id}/links/${linkId}`,
                method: 'DELETE',
            }),
        }),
        checkEventLinkAvailability: build.query<{ available: boolean; reason: 'empty' | 'format' | 'taken' | null }, { id: number; linkId: number; link: string }>({
            query: ({ id, linkId, link }) => ({
                url: `/admin/events/${id}/links/${linkId}/check?link=${encodeURIComponent(link)}`,
                method: 'GET',
            }),
        }),
        getEventTypesList: build.query<{ list: Array<{ id: string; value: string; title: { en: string; he: string }; showInDashboard: boolean; dashboardOrder: number | null; sortOrder: number | null; active: boolean }> }, void>({
            query: () => ({
                url: `/admin/event-types/list`,
                method: 'GET',
            }),
        }),
        sendUpdateEventTypesOtp: build.mutation<any, {
            items: Array<{ id: string; title?: { en: string; he: string }; showInDashboard?: boolean; dashboardOrder?: number | null; sortOrder?: number | null; active?: boolean }>
            created?: Array<{ title: { en: string; he: string }; showInDashboard?: boolean; dashboardOrder?: number | null; sortOrder?: number | null }>
        }>({
            query: ({ items, created = [] }) => ({
                url: `/admin/event-types/batch/otp`,
                method: 'POST',
                body: { items, created },
            }),
        }),
        batchUpdateEventTypes: build.mutation<any, { code: string }>({
            query: ({ code }) => ({
                url: `/admin/event-types/batch/update`,
                method: 'PUT',
                body: { code },
            }),
        }),
        deleteEvent: build.mutation({
            query: (id: string | number) => ({
                url: `/admin/events/${id}`,
                method: 'DELETE',
            }),
        }),
        uploadFile: build.mutation<{}, {id: number, data: FormData}>({
            query({id, data}) {
              return {
                url: `/admin/events/pay/${id}`,
                method: 'POST',
                body: data,
              };
            },
        }),
        downloadReport: build.query<{}, { startDate: string, endDate: string, onlyUnpaidEvent: boolean, ignoreYeshInvoice: boolean, ignoreZCredit: boolean, ignoreTransactions: boolean, debug?: boolean }>({
            query({ startDate, endDate, onlyUnpaidEvent, ignoreYeshInvoice, ignoreZCredit, ignoreTransactions, debug }) {
              return {
                url: `/admin/events/report/all?startDate=${startDate}&endDate=${endDate}&onlyUnpaidEvent=${onlyUnpaidEvent}&ignoreYeshInvoice=${ignoreYeshInvoice}&ignoreZCredit=${ignoreZCredit}&ignoreTransactions=${ignoreTransactions}&debug=${debug ? 'true' : 'false'}&language=en`,
                method: 'GET',
                responseHandler: (response: any) => response.blob()
              };
            },
        }),
        openAsAdmin: build.query<{}, {id: number}>({
            query: ({id}) => ({
                  url: `/admin/open/event/${id}`
            }),
        }),

        getEventTransactionList: build.query({
            query: ({ eventId, type, transactionId, skip, take }: { eventId: number; type: string; transactionId?: number; skip?: number; take?: number; }) => ({
                url: `/admin/events/${eventId}/transaction/list/${type}?skip=${skip || 0}&take=${take || 10}${transactionId ? `&transactionId=${transactionId}` : ''}`,
            }),
        }),

        getEventTransactionListByType: build.query({
            query: ({ type, transactionId, skip, take }: { type: string; transactionId?: number; skip?: number; take?: number; }) => ({
                url: `/admin/events/transaction/list/${type}?skip=${skip || 0}&take=${take || 10}${transactionId ? `&transactionId=${transactionId}` : ''}`,
            }),
        }),

        progressTransaction: build.mutation({
            query: ({ eventId, transactionId }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/progress`,
                method: 'PUT',
            }),
        }),

        approveTransaction: build.mutation({
            query: ({ eventId, transactionId, adminNote = null }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/confirm`,
                method: 'PUT',
                body: { adminNote },
            }),
        }),
        
        declineTransaction: build.mutation({
            query: ({ eventId, transactionId, adminNote = null }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/decline`,
                method: 'PUT',
                body: { adminNote },
            }),
        }),
        setEventTransactionComment: build.mutation<{ adminComment: string }, { eventId: number; transactionId: number; comment: string }>({
            query: ({ eventId, transactionId, comment }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/comment`,
                method: 'PUT',
                body: { comment },
            }),
        }),
        deleteEventTransactionComment: build.mutation<boolean, { eventId: number; transactionId: number }>({
            query: ({ eventId, transactionId }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/comment`,
                method: 'DELETE',
            }),
        }),
        setEventTransactionAdminNote: build.mutation<{ adminNote: string }, { eventId: number; transactionId: number; adminNote: string }>({
            query: ({ eventId, transactionId, adminNote }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/admin-note`,
                method: 'PUT',
                body: { adminNote },
            }),
        }),
        deleteEventTransactionAdminNote: build.mutation<boolean, { eventId: number; transactionId: number }>({
            query: ({ eventId, transactionId }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/admin-note`,
                method: 'DELETE',
            }),
        }),
        getTransactionReceipt: build.query({
            query: ({ eventId, transactionId }) => ({
                url: `/admin/events/${eventId}/transaction/${transactionId}/receipt`,
                method: 'GET',
                responseHandler: (response: any) => response.blob()
            }),
        }),
        getEventScannerCrossList: build.query({
            query: ({ eventId }) => ({
                url: `/admin/event/${eventId}/scanner/cross`,
                method: 'GET',
            }),
        }),
        updateEventScannerCrossList: build.mutation({
            query: ({ eventId, relatedEventLinks = [] }) => ({
                url: `/admin/event/${eventId}/scanner/cross`,
                method: 'POST',
                body: { relatedEventLinks }
            }),
        }),
        getEventTicketList: build.query({
            query: ({ eventId }) => ({
                url: `/admin/event/${eventId}/ticket/list`,
                method: 'GET',
            }),
        }),
        giftEventTickets: build.mutation({
            query: ({ eventId, users = '', smsText = '', ticketId = null, ticketName = null }) => ({
                url: `/admin/event/${eventId}/ticket/gift`,
                method: 'POST',
                body: {
                    users,
                    smsText,
                    ticketId,
                    ticketName,
                }
            }),
        }),
        getEventOwner: build.query<any, { id: number }>({
            query: ({ id }) => ({
                url: `/admin/events/${id}/owner`,
                method: 'GET',
            }),
        }),
        lookupEventByLink: build.query<any, { link: string }>({
            query: ({ link }) => ({
                url: `/admin/events/lookup/by-link?link=${encodeURIComponent(link || '')}`,
                method: 'GET',
            }),
        }),
        adminSearchUsers: build.query<Array<{ id: number; name: string; phone: string }>, { search: string }>({
            query: ({ search }) => ({
                url: `/admin/users/search?search=${encodeURIComponent(search || '')}`,
                method: 'GET',
            }),
        }),
        sendChangeEventOwnerOtp: build.mutation<any, { id: number; newOwnerId: number; keepOldOwnerAsEditor: boolean }>({
            query: ({ id, newOwnerId, keepOldOwnerAsEditor }) => ({
                url: `/admin/events/${id}/change-owner/otp`,
                method: 'POST',
                body: { newOwnerId, keepOldOwnerAsEditor },
            }),
        }),
        changeEventOwner: build.mutation<any, { id: number; newOwnerId: number; keepOldOwnerAsEditor: boolean; code: string }>({
            query: ({ id, newOwnerId, keepOldOwnerAsEditor, code }) => ({
                url: `/admin/events/${id}/change-owner`,
                method: 'PUT',
                body: { newOwnerId, keepOldOwnerAsEditor, code },
            }),
        }),
    })
})